import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Collapsible left-rail navigation. Routes are filtered by user role and
 * feature flags; clicking a destination dispatches the closeAllPopups event
 * so any open dialog dismisses on navigation.
 */
import { makeStyles, shorthands, tokens, Text } from "@fluentui/react-components";
import { useTypographyStyles } from "../../styles/typography.styles";
import {
  Home24Regular,
  Home24Filled,
  Settings24Regular,
  Settings24Filled,
  Navigation24Regular,
  ChevronLeft24Regular,
  Comment24Regular,
  Comment24Filled,
  Bot24Regular,
  Bot24Filled,
  Pulse24Regular,
  Pulse24Filled,
  PersonFeedback24Regular,
  PersonFeedback24Filled,
  Briefcase24Regular,
  Briefcase24Filled,
  PlugConnectedSettings24Regular,
  PlugConnectedSettings24Filled,
  ArrowImport24Regular,
  ArrowImport24Filled,
  ClipboardCheckmark24Regular,
  ClipboardCheckmark24Filled,
  DocumentFolder24Regular,
  DocumentFolder24Filled,
  Open24Regular,
} from "@fluentui/react-icons";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { openExternalUrl } from "../../utils/open-external-url";
import { environment } from "../../config/environment";

/**
 * Defines the structure of a navigation item
 */
interface NavItem {
  path: string;
  icon: React.ReactElement;
  activeIcon: React.ReactElement;
  label: string;
  externalUrl?: string;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * Navigation sidebar styles using FluentUI's makeStyles
 * Color scheme inspired by the Accountants Workflow Tool with dark blue-gray tones
 */
const useStyles = makeStyles({
  nav: {
    backgroundColor: "#333333", // Dark blue-gray background
    borderRight: "1px solid #374151",
    display: "flex",
    flexDirection: "column",
    paddingTop: "16px",
    paddingBottom: "16px",
    ...shorthands.gap("8px"),
    flexShrink: 0,
    transition: "width 0.3s ease", // Smooth expand/collapse animation
  },
  navCollapsed: {
    width: "60px",
    alignItems: "center",
  },
  navExpanded: {
    width: "200px",
    alignItems: "stretch",
  },
  toggleButton: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    color: "#d1d5db",
    transition: "all 0.2s ease",
    marginLeft: "6px",
    marginRight: "6px",
    marginBottom: "8px",
    ":hover": {
      backgroundColor: "#1f2937",
      color: "#ffffff",
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: "#3b82f6",
      outlineOffset: "2px",
    },
  },
  navItems: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },
  divider: {
    height: "1px",
    backgroundColor: "#4b5563",
    marginLeft: "14px",
    marginRight: "14px",
    marginTop: "4px",
    marginBottom: "4px",
    opacity: 0.5,
  },
  bottomItems: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
    marginTop: "auto",
  },
  navButton: {
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    color: "#d1d5db",
    transition: "all 0.2s ease",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    marginLeft: "6px",
    marginRight: "6px",
    ...shorthands.padding("8px", "12px"),
    ":hover": {
      backgroundColor: "#1f2937",
      color: "#ffffff",
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: "#3b82f6",
      outlineOffset: "2px",
    },
  },
  navButtonCollapsed: {
    justifyContent: "center",
    ...shorthands.padding("8px"),
  },
  navButtonExpanded: {
    justifyContent: "flex-start",
    ...shorthands.gap("12px"),
  },
  navButtonActive: {
    backgroundColor: "#2563eb", // Blue accent for active state
    color: "#ffffff",
    fontWeight: tokens.fontWeightSemibold,
    ":hover": {
      backgroundColor: "#1d4ed8",
      color: "#ffffff",
    },
  },
  // Applied when the workbook isn't linked yet. Non-Setup/Settings nav is
  // unclickable so the user can't reach pages that need a spreadsheet ID.
  // Sidebar stays visible so Setup/Settings/Feedback are still one click away.
  navButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    ":hover": {
      backgroundColor: "transparent",
      color: "#d1d5db",
    },
  },
  navButtonIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  navButtonLabel: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  footer: {
    ...shorthands.padding("12px"),
    ...shorthands.borderTop("1px", "solid", "#374151"),
    marginTop: tokens.spacingVerticalS,
  },
  footerContent: {
    fontSize: tokens.fontSizeBase200,
    color: "#9ca3af",
    textAlign: "center",
  },
  footerExpanded: {
    textAlign: "left",
  },
  footerLogos: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: "0px",
    marginBottom: tokens.spacingVerticalS,
  },
  footerLogo: {
    height: "48px",
    width: "auto",
  },
});

/**
 * Navigation sidebar component with collapsible functionality
 * Features:
 * - Collapsible design (60px collapsed, 200px expanded)
 * - Dark blue-gray theme for reduced eye strain
 * - Active state highlighting
 * - Smooth transitions
 * - Accessibility support (ARIA attributes, keyboard navigation)
 */
const NavigationSidebar: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(true);
  const { enableAI } = useFeatureFlags();
  const { spreadsheetId, isStandalone } = useWorkflowIntelligence();

  // When the workbook isn't linked yet, only Setup / Settings / Feedback are
  // reachable — everything else depends on a real spreadsheet ID. The routes
  // are also protected by RequireSpreadsheet in App.tsx; this is the visual
  // half of that lock.
  const isWorkbookLinked = !!spreadsheetId;
  const alwaysReachable = new Set(["/setup", "/settings", "/feedback"]);
  const isNavDisabled = (path: string) => !isWorkbookLinked && !alwaysReachable.has(path);

  /**
   * Navigation items configuration, grouped by category
   * Add new routes here to extend the navigation menu
   */
  const navGroups: NavGroup[] = [
    {
      items: [
        {
          path: "/",
          icon: <Home24Regular />,
          activeIcon: <Home24Filled />,
          label: "Home",
        },
      ],
    },
    {
      label: "Workflow",
      items: [
        {
          path: "/workflow-status",
          icon: <Pulse24Regular />,
          activeIcon: <Pulse24Filled />,
          label: "Workflow Status",
        },
        {
          path: "/discussions",
          icon: <Comment24Regular />,
          activeIcon: <Comment24Filled />,
          label: "Discussions",
        },
        {
          path: "/review",
          icon: <ClipboardCheckmark24Regular />,
          activeIcon: <ClipboardCheckmark24Filled />,
          label: "Review",
        },
        {
          path: "/documents",
          icon: <DocumentFolder24Regular />,
          activeIcon: <DocumentFolder24Filled />,
          label: "Documents",
        },
      ],
    },
    ...(enableAI
      ? [
          {
            label: "AI",
            items: [
              {
                path: "/tasks",
                icon: <Bot24Regular />,
                activeIcon: <Bot24Filled />,
                label: "Agents",
              },
              // Incoming Changes is entirely about applying cell edits back
              // into a live workbook — meaningless in standalone (nothing to
              // apply the changes to). Hidden from the sidebar there.
              ...(isStandalone
                ? []
                : [{
                    path: "/incoming-changes",
                    icon: <ArrowImport24Regular />,
                    activeIcon: <ArrowImport24Filled />,
                    label: "Incoming Changes",
                  }]),
            ],
          },
        ]
      : []),
  ];

  /**
   * Bottom navigation items - Settings and Feedback displayed at the bottom
   * Settings acts as a buffer to prevent accidental clicks on Feedback (which opens external browser)
   */
  const bottomNavItems: NavItem[] = [
    {
      path: "/setup",
      icon: <Briefcase24Regular />,
      activeIcon: <Briefcase24Filled />,
      label: "Setup",
    },
    {
      path: "/account-mappings",
      icon: <PlugConnectedSettings24Regular />,
      activeIcon: <PlugConnectedSettings24Filled />,
      label: "Account Mapping",
    },
    {
      path: "/settings",
      icon: <Settings24Regular />,
      activeIcon: <Settings24Filled />,
      label: "Settings",
    },
    {
      path: "/feedback",
      icon: <PersonFeedback24Regular />,
      activeIcon: <PersonFeedback24Filled />,
      label: "Feedback",
      externalUrl:
        "https://bdonz-digital.atlassian.net/jira/core/form/991a5a53-8cb3-4beb-bcf0-29d104530985?atlOrigin=eyJpIjoiOTk3OWRiNDljOGI1NDE5ZDlkNmRiMzljZDRlMDk2YWYiLCJwIjoiaiJ9",
    },
  ];

  /**
   * Determines if a navigation item is currently active
   * Special handling for home route to match exact path
   */
  const isActive = (path: string): boolean => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  /**
   * Toggles the sidebar between collapsed and expanded states
   */
  const handleToggle = (): void => {
    setCollapsed(!collapsed);
  };

  return (
    <nav
      className={`${styles.nav} ${collapsed ? styles.navCollapsed : styles.navExpanded}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Collapse/Expand Toggle Button */}
      <button
        type="button"
        className={styles.toggleButton}
        onClick={handleToggle}
        title={collapsed ? "Expand menu" : "Collapse menu"}
        aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        aria-expanded={collapsed ? "false" : "true"}
      >
        <div className={styles.navButtonIcon}>{collapsed ? <Navigation24Regular /> : <ChevronLeft24Regular />}</div>
        {!collapsed && <Text className={styles.navButtonLabel}>Menu</Text>}
      </button>

      {/* Navigation Items */}
      <div className={styles.navItems}>
        {navGroups.map((group, groupIndex) => (
          <React.Fragment key={group.label ?? groupIndex}>
            {groupIndex > 0 && <div className={styles.divider} />}
            {group.items.map((item) => {
              const active = isActive(item.path);
              const disabled = isNavDisabled(item.path);
              return (
                <button
                  type="button"
                  key={item.path}
                  className={`${styles.navButton} ${collapsed ? styles.navButtonCollapsed : styles.navButtonExpanded} ${
                    active ? styles.navButtonActive : ""
                  } ${disabled ? styles.navButtonDisabled : ""}`}
                  onClick={() =>
                    item.externalUrl
                      ? openExternalUrl(item.externalUrl)
                      : navigate(item.path)
                  }
                  disabled={disabled}
                  title={collapsed ? item.label : (disabled ? "Complete Setup to unlock" : undefined)}
                  aria-label={item.label}
                  aria-current={active ? "page" : undefined}
                >
                  <div className={styles.navButtonIcon}>{active ? item.activeIcon : item.icon}</div>
                  {!collapsed && <Text className={styles.navButtonLabel}>{item.label}</Text>}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Bottom Items - Settings and Feedback */}
      <div className={styles.bottomItems}>
        {/* Pop out — Excel-only. Opens the addin in a new browser tab with
            ?standalone=1&spreadsheetId=<current>, so users can keep working
            on a second monitor while their workbook stays open in Excel.
            Uses environment.urls.standaloneOrigin — the BDO custom domain
            in dev/prod, or the current runtime origin locally — so users
            see wi(-dev).bdo.co.nz instead of the raw SWA hostname.
            Disabled until we have a spreadsheetId to pass. */}
        {!isStandalone && spreadsheetId && (
          <button
            type="button"
            key="/pop-out"
            className={`${styles.navButton} ${collapsed ? styles.navButtonCollapsed : styles.navButtonExpanded}`}
            onClick={() => {
              const url = `${environment.urls.standaloneOrigin}${window.location.pathname}?standalone=1&spreadsheetId=${encodeURIComponent(spreadsheetId)}`;
              openExternalUrl(url);
            }}
            title={collapsed ? "Pop out to browser" : undefined}
            aria-label="Pop out to browser"
          >
            <div className={styles.navButtonIcon}><Open24Regular /></div>
            {!collapsed && <Text className={styles.navButtonLabel}>Pop out</Text>}
          </button>
        )}
        {bottomNavItems.map((item) => {
          const active = isActive(item.path);
          const disabled = isNavDisabled(item.path);
          return (
            <button
              type="button"
              key={item.path}
              className={`${styles.navButton} ${collapsed ? styles.navButtonCollapsed : styles.navButtonExpanded} ${
                active ? styles.navButtonActive : ""
              } ${disabled ? styles.navButtonDisabled : ""}`}
              onClick={() =>
                item.externalUrl
                  ? window.open(item.externalUrl, "_blank", "noopener,noreferrer")
                  : navigate(item.path)
              }
              disabled={disabled}
              title={collapsed ? item.label : (disabled ? "Complete Setup to unlock" : undefined)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
            >
              <div className={styles.navButtonIcon}>{active ? item.activeIcon : item.icon}</div>
              {!collapsed && <Text className={styles.navButtonLabel}>{item.label}</Text>}
            </button>
          );
        })}
      </div>

      {/* Footer - App Info (shown when expanded) */}
      {!collapsed && (
        <div className={styles.footer}>
          <div className={styles.footerLogos}>
            <img src="assets/bdo-logo-white.png" alt="BDO Logo" className={styles.footerLogo} />
            <img src="assets/icon-1024-white.png" alt="App Icon" className={styles.footerLogo} />
          </div>
          <div className={`${styles.footerContent} ${styles.footerExpanded}`}>
            <Text className={typography.bodySmall}>
              Workflow Intelligence
            </Text>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationSidebar;
